'use client';
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { setModalName, setModalVisible } from '@/lib/slices/modal-slice';
import { setActiveCourseData } from '@/lib/slices/course-slice';

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { restrictToParentElement } from '@dnd-kit/modifiers';

import UnitCard from './display/UnitCard';
import Loading from '@/common/components/Loading';
import { ChevronDownIcon, ChevronUpIcon, ChevronLeftIcon } from '@/assets/icons/chevron';
import { AddContentButton } from '@/common/components/course';

import UserCourseStorageService from '@/services/storage/user-course-storage-service';
import CourseUtilityService from '@/services/course/course-utility-service';
import CourseReorderService from '@/services/course/course-reorder-service';
import { UnitData } from 'course-types';
import { Id } from 'lesson-types';
import LoadingSpinner from '../LoadingSpinner';
import CourseOverviewSkeleton from '@/(portal)/admin/(pages)/courses/components/course-management/skeletons/CourseOverviewSkeleton';

const propTypes = {
  id: PropTypes.string.isRequired,
  courseName: PropTypes.string.isRequired,
  isAdminView: PropTypes.bool,
};

interface Props {
  id: Id;
  courseName: string;
  isAdminView?: boolean;
  isEditable?: boolean;
}

function CourseOverview({ id, courseName, isAdminView = false, isEditable = true }: Props) {
  const [units, setUnits] = useState<Array<[string, UnitData]>>([]);
  const [expandedUnits, setExpandedUnits] = useState<{ [key: string]: boolean }>({});
  const [isDragging, setIsDragging] = useState(false);
  const [expandAllTrigger, setExpandAllTrigger] = useState<number>(0);
  const [collapseAllTrigger, setCollapseAllTrigger] = useState<number>(0);

  const dispatch = useAppDispatch();
  const store = useAppStore();
  const activeCourse = useAppSelector(() => store.getState().course.activeCourseData);

  // Drag and drop sensors
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));

  // Calculate total activities across all units
  const totalActivities = CourseUtilityService.calculateTotalActivities(units);
  const totalResources = CourseUtilityService.calculateTotalResources(units);

  useEffect(() => {
    const fetchData = async () => {
      await pullCourseData();
      await setCourseUnits();
    };
    fetchData();
  }, [id]);

  // Update units when Redux data changes (for real-time updates from modals)
  useEffect(() => {
    if (activeCourse?.id === id && activeCourse?.units) {
      const unitsArray = Object.entries(activeCourse.units) as Array<[string, UnitData]>;
      unitsArray.sort((unitA, unitB) => unitA[1].index - unitB[1].index);
      setUnits(unitsArray);
    }
  }, [activeCourse?.units, id]);

  // Track expanded units for expand/collapse all functionality
  const handleExpandedChange = useCallback((unitId: string, expanded: boolean) => {
    setExpandedUnits((prev) => ({ ...prev, [unitId]: expanded }));
  }, []);

  const pullCourseData = async () => {
    if (id !== '') {
      const treeCookie = UserCourseStorageService.getCourseData();
      if (treeCookie === undefined || treeCookie?.id !== id) {
        await UserCourseStorageService.setCourseData(id);
      }
    }
  };

  //assign state from course-map cookie data
  const setCourseUnits = async (): Promise<void> => {
    const courseData = UserCourseStorageService.getCourseData();
    if (courseData != undefined) {
      const unitsArray = Object.entries(courseData.units ? courseData?.units : {}) as Array<
        [string, UnitData]
      >;

      unitsArray.sort((unitA, unitB) => unitA[1].index - unitB[1].index);
      setUnits(unitsArray);
    }
  };

  const handleCollapseAll = () => {
    setCollapseAllTrigger((prev) => prev + 1);
  };

  const handleExpandAll = () => {
    setExpandAllTrigger((prev) => prev + 1);
  };

  // Open modal for adding new unit
  const onAddUnit = () => {
    dispatch(setModalVisible(true));
    dispatch(setModalName('AddNewUnit'));
  };

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) {
      // Dropped outside of a valid target, reset and exit
      setIsDragging(false);
      return;
    }

    if (active.id !== over?.id) {
      let newItems: Array<[string, UnitData]> = [];
      let oldIndex = -1;
      let newIndex = -1;
      let originalItems: Array<[string, UnitData]> = [];

      // Update state immediately for responsive UI
      setUnits((items) => {
        originalItems = items; // Store original reference for potential revert
        oldIndex = items.findIndex(([unitId]) => unitId === active.id);
        newIndex = items.findIndex(([unitId]) => unitId === over?.id);

        // Reorder the array while preserving exact object references
        const reorderedItems = [...items]; // Create new array
        const movedItem = reorderedItems[oldIndex]; // Get exact reference
        reorderedItems.splice(oldIndex, 1); // Remove from old position
        reorderedItems.splice(newIndex, 0, movedItem); // Insert at new position

        newItems = reorderedItems;
        return reorderedItems;
      });

      // Send reorder request to backend using the reorder service
      const unitRecord: { [unitId: string]: number } = {};
      newItems.forEach(([unitId], index) => {
        unitRecord[unitId] = index;
      });

      const result = await CourseReorderService.reorderUnits(unitRecord, id);

      if (!result.success) {
        // Revert the UI changes on error
        setUnits(originalItems);
      } else {
        console.log('Units reordered successfully:', {
          unitId: active.id,
          from: oldIndex,
          to: newIndex,
        });
        // Update Redux activeCourseData indices to reflect new order
        if (activeCourse?.id === id && activeCourse?.units) {
          dispatch(setActiveCourseData(result.courseData));
        }
      }
    }

    // Reset dragging state
    setIsDragging(false);
  };

  if (id === '') {
    return <CourseOverviewSkeleton />;
  }

  return (
    <div className='w-full flex flex-col max-w-6xl mx-auto px-6 gap-4'>
      {/* Back to Courses */}
      {!isAdminView && (
        <button
          type='button'
          onClick={() => window.history.back()}
          className='flex items-center gap-1.5 w-fit text-xs font-semibold mb-1'
          style={{ color: '#1570EF' }}
        >
          <ChevronLeftIcon className='w-4 h-4' />
          Back to Courses
        </button>
      )}

      {/* Header with controls */}
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-3'>
          {units.length > 0 && (
            <button
              onClick={
                units.every(([unitId]) => expandedUnits[unitId] === true)
                  ? handleCollapseAll
                  : handleExpandAll
              }
              className='flex items-center gap-2 px-4 py-2 text-xs font-semibold text-text_gray_700 rounded-lg border border-gray_300 shadow-sm'
              style={{
                borderRadius: '8px',
                border: '1px solid #D5D7DA',
                background: '#FFF',
                boxShadow: '0 1px 3px 0 rgba(10, 13, 18, 0.10), 0 1px 2px 0 rgba(10, 13, 18, 0.06)',
              }}
            >
              {units.every(([unitId]) => expandedUnits[unitId] === true) ? (
                <>
                  <ChevronUpIcon className='w-4 h-4' />
                  Collapse all
                </>
              ) : (
                <>
                  <ChevronDownIcon className='w-4 h-4' />
                  Expand all
                </>
              )}
            </button>
          )}
        </div>
        <p className='text-xs font-semibold text-text_gray_600'>
          {units.length} units • {totalActivities} activit{totalActivities === 1 ? 'y' : 'ies'}
          {totalResources > 0
            ? ` • ${totalResources} ${totalResources === 1 ? 'resource' : 'resources'}`
            : ''}
        </p>
      </div>

      {/* Units Grid */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToParentElement]}
      >
        <SortableContext
          items={units.map(([unitId]) => unitId)}
          strategy={verticalListSortingStrategy}
        >
          <div className='space-y-6'>
            {units.map((unit, index) => {
              const unitId = unit[0];

              return (
                <UnitCard
                  key={unitId}
                  unit={unit[1]}
                  unitId={unitId}
                  initialExpanded={false}
                  onExpandedChange={handleExpandedChange}
                  expandAll={expandAllTrigger}
                  collapseAll={collapseAllTrigger}
                  isAdminView={isAdminView}
                  isEditable={isEditable}
                />
              );
            })}
          </div>
        </SortableContext>
      </DndContext>

      {/* Add Unit Button */}
      {isAdminView && isEditable && (
        <div className='flex justify-center w-full'>
          <AddContentButton text='Add Unit' onClick={onAddUnit} fullWidth />
        </div>
      )}

      <div className='h-16' />
    </div>
  );
}

CourseOverview.propTypes = propTypes;

export default CourseOverview;
